import Chip from 'core-system/Chip'
import InfoIcon from 'core-system/Icons/Misc/Info'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import SvgTooltip from 'core-system/Tooltip/SvgTooltip'
import ReceiptLink from 'features/Reimbursements/components/shared/ReceiptLink'
import React, { useMemo } from 'react'
import type { ReimbursementSubmission } from 'redux/reimbursements/reimbursementsTypes'
import styled from 'styled-components'
import { useDrawerTableOperations } from './useDrawerTableOperations'
import {
  buildReimbursementRow,
  formatReimbursementMoney,
  pendingReimbursements,
  processedReimbursements,
  REIMBURSEMENT_SEARCH_KEYS,
  reimbursementFilters,
  reimbursementStatusVariant,
  resolutionCaption,
  summarizeReimbursements,
} from './commuterReimbursementRows'

const Section = styled.div`
  margin-bottom: 1.5rem;
`

const SectionHeader = styled.div`
  margin-bottom: 0.75rem;
`

const CardRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`

const SummaryCard = styled.div<{ $highlighted?: boolean }>`
  min-width: 0;
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid
    ${(props) =>
      props.$highlighted
        ? props.theme.palette.warning.yellow500
        : props.theme.palette.grey.grey300};
  background-color: ${(props) =>
    props.$highlighted
      ? props.theme.palette.warning.yellow50
      : props.theme.palette.white};
`

const StatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`

const HeaderCell = ({ text }: { text: string }) => (
  <TableRowCell noWrap>
    <Text variant='action4' fontWeight={600}>
      {text}
    </Text>
  </TableRowCell>
)

interface CommuterReimbursementsProps {
  receipts: ReimbursementSubmission[] | null
  loading?: boolean
  error?: string | null
}

const CommuterReimbursements = ({
  receipts,
  loading = false,
  error = null,
}: CommuterReimbursementsProps) => {
  const rows = receipts || []
  const summary = summarizeReimbursements(rows)
  const pendingRows = useMemo(
    () => pendingReimbursements(receipts || []).map(buildReimbursementRow),
    [receipts]
  )
  const processedRows = useMemo(
    () => processedReimbursements(receipts || []).map(buildReimbursementRow),
    [receipts]
  )
  const filterSource = useMemo(
    () => [...pendingRows, ...processedRows],
    [pendingRows, processedRows]
  )
  const { apply, bar } = useDrawerTableOperations({
    source: filterSource,
    searchKeys: REIMBURSEMENT_SEARCH_KEYS,
    sortKey: 'tripDateKey',
    buildFilters: reimbursementFilters,
    margin: '0 0 1.5rem',
  })
  const visiblePending = apply(pendingRows)
  const visibleProcessed = apply(processedRows)

  return (
    <>
      <CardRow>
        <SummaryCard $highlighted>
          <Text variant='body2' textColor={palette.text.secondary}>
            Pending
          </Text>
          <Text variant='h4'>{formatReimbursementMoney(summary.pendingAmount)}</Text>
          <Text variant='body2' textColor={palette.text.secondary}>
            {summary.pendingCaption}
          </Text>
        </SummaryCard>
        <SummaryCard>
          <Text variant='body2' textColor={palette.text.secondary}>
            Approved
          </Text>
          <Text variant='h4'>{formatReimbursementMoney(summary.approvedAmount)}</Text>
          <Text variant='body2' textColor={palette.text.secondary}>
            {summary.approvedCaption}
          </Text>
        </SummaryCard>
        <SummaryCard>
          <Text variant='body2' textColor={palette.text.secondary}>
            Denied
          </Text>
          <Text variant='h4'>{formatReimbursementMoney(summary.deniedAmount)}</Text>
          <Text variant='body2' textColor={palette.text.secondary}>
            {summary.deniedCaption}
          </Text>
        </SummaryCard>
      </CardRow>
      {loading && receipts == null ? (
        <Section>
          <Text variant='body2' textColor={palette.text.secondary}>
            Loading reimbursements
          </Text>
        </Section>
      ) : null}
      {error && !loading && receipts == null ? (
        <Section>
          <Text variant='body2' textColor={palette.text.secondary}>
            Couldn&apos;t load reimbursements
          </Text>
        </Section>
      ) : null}
      {bar}
      <Section>
        <SectionHeader>
          <Text variant='h5'>Pending</Text>
        </SectionHeader>
        <Table
          maxRows={5}
          emptyMessage={
            visiblePending.length === 0 && pendingRows.length > 0
              ? 'No matching reimbursements'
              : 'No pending reimbursements'
          }
          headerCells={[
            <TableRow key='pending-header'>
              <HeaderCell text='Trip date' />
              <HeaderCell text='Category' />
              <HeaderCell text='Requested' />
              <HeaderCell text='Status' />
              <HeaderCell text='Receipt' />
            </TableRow>,
          ]}
          rows={visiblePending.map((row) => (
            <TableRow key={row.id}>
              <TableRowCell noWrap>
                <Text variant='action3'>{row.tripDate}</Text>
              </TableRowCell>
              <TableRowCell>
                <Text variant='action3'>{row.category}</Text>
              </TableRowCell>
              <TableRowCell noWrap>
                <Text variant='action3'>{row.requested}</Text>
              </TableRowCell>
              <TableRowCell>
                <Chip variant={reimbursementStatusVariant(row.receipt.status)}>
                  {row.status}
                </Chip>
              </TableRowCell>
              <TableRowCell>
                <ReceiptLink receiptUrl={row.receipt.receiptUrl} stopPropagation />
              </TableRowCell>
            </TableRow>
          ))}
        />
      </Section>
      <Section>
        <SectionHeader>
          <Text variant='h5'>Processed & denied</Text>
        </SectionHeader>
        <Table
          maxRows={5}
          emptyMessage={
            visibleProcessed.length === 0 && processedRows.length > 0
              ? 'No matching reimbursements'
              : 'No processed reimbursements'
          }
          headerCells={[
            <TableRow key='processed-header'>
              <HeaderCell text='Trip date' />
              <HeaderCell text='Category' />
              <HeaderCell text='Requested' />
              <HeaderCell text='Approved' />
              <HeaderCell text='Status' />
              <HeaderCell text='Receipt' />
            </TableRow>,
          ]}
          rows={visibleProcessed.map((row) => {
            const caption = resolutionCaption(row.receipt)
            return (
              <TableRow key={row.id}>
                <TableRowCell noWrap>
                  <Text variant='action3'>{row.tripDate}</Text>
                </TableRowCell>
                <TableRowCell>
                  <Text variant='action3'>{row.category}</Text>
                  {caption ? (
                    <Text variant='body2' textColor={palette.text.secondary}>
                      {caption}
                    </Text>
                  ) : null}
                </TableRowCell>
                <TableRowCell noWrap>
                  <Text variant='action3'>{row.requested}</Text>
                </TableRowCell>
                <TableRowCell noWrap>
                  <Text variant='action3'>{row.approved}</Text>
                </TableRowCell>
                <TableRowCell>
                  <StatusLine>
                    <Chip variant={reimbursementStatusVariant(row.receipt.status)}>
                      {row.status}
                    </Chip>
                    {row.receipt.note ? (
                      <SvgTooltip
                        svg={
                          <InfoIcon
                            width={16}
                            height={16}
                            color={palette.text.secondary}
                            aria-label='Claim note'
                          />
                        }
                        description={row.receipt.note}
                        isMultiLine
                        height='1rem'
                      />
                    ) : null}
                  </StatusLine>
                </TableRowCell>
                <TableRowCell>
                  <ReceiptLink
                    receiptUrl={row.receipt.receiptUrl}
                    stopPropagation
                  />
                </TableRowCell>
              </TableRow>
            )
          })}
        />
      </Section>
    </>
  )
}

export default CommuterReimbursements
